import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { formatPrice, formatPriceRange, truncateText, getDomain } from '@/lib/utils';
import { Product } from '@/types';
import { NotificationPopup } from '@/components/notifications/notification-prompt';
import { Button } from '@/components/ui/button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

interface CategoryWithImage {
  name: string;
  image: string;
}



export function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryWithImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { storeInfo } = useStoreInfo();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
        const data = await response.json();
        
        if (data.status) {
          const products = data.products.filter((p: Product) => p.status === 1);
          setFeaturedProducts(products.slice(0, 24));
          
          const categoriesMap = new Map<string, string>();
          products.forEach((product: Product) => {
            if (product.category && !categoriesMap.has(product.category) && product.images?.length > 0) {
              categoriesMap.set(product.category, product.images[0]);
            }
          });

          const categoriesWithImages = Array.from(categoriesMap).map(([name, image]) => ({
            name,
            image
          }));

          setCategories(categoriesWithImages);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate('/products', { state: { searchTerm: searchTerm.trim() } });
    }
  };

  const heroImages = storeInfo?.heroSlider?.map(image => 
    `https://marivieint.com/${image}`
  ) || [];

  return (
    <>
      <Helmet>
        <title>Plymouth Braider - Premium Hair & Beauty</title>
        <meta name="description" content="Your premier destination for luxury wigs, braiding, and hair styling products" />
      </Helmet>

      <NotificationPopup />

      {/* Hero Section */}
      <div className="relative bg-brand-50">
        <Swiper
          modules={[Pagination, Autoplay, EffectFade]}
          pagination={{ clickable: true }}
          autoplay={{ delay: 5000 }}
          effect="fade"
          className="h-[70vh] md:h-[80vh]"
        >
          {heroImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full w-full">
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-xl">
                      <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-display">
                        Discover Your Perfect Style
                      </h1>
                      <p className="text-xl text-white/90 mb-8">
                        Premium wigs, braids, and hair care products for the modern woman
                      </p>
                      <Link to="/products">
                        <Button size="lg" className="text-lg">
                          Shop Now
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 shadow-lg relative z-10 -mt-10">
        <div className="max-w-xl mx-auto px-4 py-6">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-full border-none ring-2 ring-pink-100 dark:ring-pink-900 focus:ring-brand-primary dark:focus:ring-brand-light text-lg dark:bg-gray-700 dark:text-white"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
          </form>
        </div>
      </div>



      {/* Categories Grid */}
      <div className="py-16 bg-brand-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            Shop by Category
          </h2>
          <div className={`grid gap-6 category-grid ${
            categories.length <= 2 ? 'md:max-w-2xl mx-auto' : ''
          } ${
            categories.length === 1 ? 'grid-cols-1' :
            categories.length === 2 ? 'grid-cols-2' :
            categories.length === 3 ? 'grid-cols-3' :
            'grid-cols-2 md:grid-cols-4'
          }`}>
            {categories.map((category, index) => (
              <Link
                key={index}
                to={`/products?category=${encodeURIComponent(category.name)}`}
                className="category-item group relative overflow-hidden rounded-2xl aspect-square"
                style={{ '--category-index': index } as React.CSSProperties}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-colors" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white text-center">
                    {category.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Products */}
      <div className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Featured Products
            </h2>
            <Link to="/products">
              <Button variant="outline">View All</Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
            {loading ? (
              [...Array(24)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-2xl mb-4" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              ))
            ) : (
              featuredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-2xl">
                    <img
                      src={product.images[0]}
                      alt={product.produt_name}
                      className="w-full aspect-square object-cover transform group-hover:scale-110 transition-transform duration-300"
                    />
                    {product.postType === "1" && (
                      <div className="absolute top-2 right-2 bg-brand-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                        New Arrival
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-brand-primary transition-colors">
                      {truncateText(product.produt_name, 50)}
                    </h3>
                    <p className="text-brand-primary dark:text-brand-light font-bold mt-2">
                      {formatPrice(product.sellingprice)}
                    </p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}